import { useState, useEffect, useCallback } from 'react'
import {
  Users, RefreshCw, Search, TrendingUp, TrendingDown, Minus,
  Building2, Code, Shield, Cloud, Brain, Cpu,
} from 'lucide-react'
import { clsx } from 'clsx'
import { formatDistanceToNow } from 'date-fns'
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from 'recharts'
import { api } from '../lib/api'

function relativeTime(ts: string): string {
  if (!ts) return ''
  try { return formatDistanceToNow(new Date(ts), { addSuffix: false }) + ' ago' } catch { return ts }
}

const JOB_CATEGORIES: Record<string, { keywords: string[]; icon: React.ElementType; color: string }> = {
  'AI/ML Engineer': { keywords: ['ai engineer', 'ml engineer', 'machine learning engineer', 'deep learning', 'llm engineer', 'ai researcher'], icon: Brain, color: '#8b5cf6' },
  'Platform Engineer': { keywords: ['platform engineer', 'devops', 'sre', 'site reliability', 'infrastructure engineer', 'cloud engineer'], icon: Cloud, color: '#06b6d4' },
  'Security Engineer': { keywords: ['security engineer', 'cybersecurity', 'appsec', 'infosec', 'penetration tester', 'soc analyst', 'security architect'], icon: Shield, color: '#f43f5e' },
  'Data Engineer': { keywords: ['data engineer', 'data pipeline', 'etl', 'data platform', 'analytics engineer', 'dbt'], icon: Cpu, color: '#f59e0b' },
  'Backend Engineer': { keywords: ['backend engineer', 'backend developer', 'go developer', 'rust developer', 'python developer', 'java developer'], icon: Code, color: '#10b981' },
  'Frontend Engineer': { keywords: ['frontend engineer', 'frontend developer', 'react developer', 'ui engineer'], icon: Code, color: '#3b82f6' },
  'Full Stack': { keywords: ['full stack', 'fullstack', 'full-stack'], icon: Code, color: '#64748b' },
  'CTO/VP Eng': { keywords: ['cto', 'vp engineering', 'head of engineering', 'engineering director', 'chief technology'], icon: Building2, color: '#ec4899' },
  'Product Manager': { keywords: ['product manager', 'product lead', 'pm role', 'product owner'], icon: Building2, color: '#a78bfa' },
  'Blockchain/Web3': { keywords: ['blockchain developer', 'solidity', 'web3 developer', 'smart contract', 'defi developer'], icon: Code, color: '#f97316' },
}

const LAYOFF_KEYWORDS = ['layoff', 'laid off', 'job cut', 'workforce reduction', 'restructuring', 'downsizing', 'rif', 'redundanc']
const HIRING_KEYWORDS = ['hiring', 'we\'re hiring', 'job opening', 'join us', 'looking for', 'new role', 'open position']
const CHART_COLORS = ['#8b5cf6', '#06b6d4', '#f43f5e', '#f59e0b', '#10b981', '#3b82f6', '#64748b', '#ec4899', '#a78bfa', '#f97316']

export default function TalentPage() {
  const [articles, setArticles] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [tab, setTab] = useState<'trends' | 'feed' | 'layoffs'>('trends')

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await api.get('/api/news/feed', { params: { limit: 200 } })
      setArticles(data.articles || [])
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { fetchData() }, [fetchData])

  // Analyse
  const categoryCounts: Record<string, number> = {}
  const categoryArticles: Record<string, any[]> = {}
  let layoffCount = 0, hiringCount = 0
  const layoffArticles: any[] = [], hiringArticles: any[] = []

  for (const article of articles) {
    const text = ((article.title || '') + ' ' + (article.summary || '')).toLowerCase()
    for (const [cat, config] of Object.entries(JOB_CATEGORIES)) {
      if (config.keywords.some(kw => text.includes(kw))) {
        categoryCounts[cat] = (categoryCounts[cat] || 0) + 1
        if (!categoryArticles[cat]) categoryArticles[cat] = []
        categoryArticles[cat].push(article)
      }
    }
    if (LAYOFF_KEYWORDS.some(kw => text.includes(kw))) { layoffCount++; layoffArticles.push(article) }
    if (HIRING_KEYWORDS.some(kw => text.includes(kw))) { hiringCount++; hiringArticles.push(article) }
  }

  const sortedCategories = Object.entries(categoryCounts).sort((a, b) => b[1] - a[1]).map(([name, count]) => ({ name, count }))
  const talentFeed = articles.filter(a =>
    ['Stack Overflow Blog', 'DEV Community', 'Dice Tech', 'Hacker News'].includes(a.source) ||
    LAYOFF_KEYWORDS.some(kw => ((a.title || '') + (a.summary || '')).toLowerCase().includes(kw)) ||
    HIRING_KEYWORDS.some(kw => ((a.title || '') + (a.summary || '')).toLowerCase().includes(kw))
  ).filter(a => !searchTerm || a.title?.toLowerCase().includes(searchTerm.toLowerCase()))

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-text-primary flex items-center gap-2">
            <Users size={20} className="text-blue" /> Talent & Jobs Intelligence
          </h1>
          <p className="text-[10px] text-text-secondary">Job category trends, hiring/layoff signals from {articles.length} articles</p>
        </div>
        <button type="button" onClick={fetchData} className="btn-secondary text-xs">
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="stat-card py-2 px-3"><span className="stat-label text-[9px]">Categories</span><span className="text-lg font-bold text-text-primary">{sortedCategories.length}</span></div>
        <div className="stat-card py-2 px-3"><span className="stat-label text-[9px]">Hiring</span><span className="text-lg font-bold text-emerald">{hiringCount}</span></div>
        <div className="stat-card py-2 px-3"><span className="stat-label text-[9px]">Layoffs</span><span className="text-lg font-bold text-rose">{layoffCount}</span></div>
        <div className="stat-card py-2 px-3"><span className="stat-label text-[9px]">Sentiment</span><span className={clsx('text-lg font-bold', hiringCount > layoffCount ? 'text-emerald' : 'text-rose')}>{hiringCount > layoffCount ? 'HIRING' : 'LAYOFFS'}</span></div>
      </div>

      <div className="flex gap-2">
        {([{ key: 'trends' as const, label: 'Category Trends' }, { key: 'feed' as const, label: `Feed (${talentFeed.length})` }, { key: 'layoffs' as const, label: `Layoffs & Hiring` }]).map(t => (
          <button key={t.key} type="button" onClick={() => setTab(t.key)} className={clsx('badge text-[10px] cursor-pointer px-3 py-1.5', tab === t.key ? 'badge-blue' : 'bg-surface-card text-text-muted border border-border')}>{t.label}</button>
        ))}
      </div>

      {tab === 'trends' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="card p-4">
            <h2 className="text-xs font-semibold text-text-primary mb-3">Job Category Mentions</h2>
            {sortedCategories.length > 0 ? (
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={sortedCategories} layout="vertical" margin={{ left: 10, right: 20 }}>
                  <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 10 }} />
                  <YAxis type="category" dataKey="name" width={130} tick={{ fill: '#94a3b8', fontSize: 9 }} />
                  <Tooltip contentStyle={{ backgroundColor: '#18181c', border: '1px solid #26262c', borderRadius: 8, fontSize: 11 }} />
                  <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                    {sortedCategories.map((_, idx) => <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-xs text-text-muted text-center py-8">No data</p>}
          </div>
          <div className="card p-4">
            <h2 className="text-xs font-semibold text-text-primary mb-3">Hot Roles</h2>
            <div className="space-y-3 max-h-[350px] overflow-y-auto">
              {sortedCategories.slice(0, 8).map(cat => {
                const config = JOB_CATEGORIES[cat.name]
                const Icon = config?.icon || Code
                return (
                  <div key={cat.name} className="border-b border-border pb-2 last:border-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Icon size={12} style={{ color: config?.color }} />
                      <span className="text-xs font-semibold text-text-primary">{cat.name}</span>
                      <span className="badge-blue text-[9px] ml-auto">{cat.count}</span>
                    </div>
                    {(categoryArticles[cat.name] || []).slice(0, 2).map((a: any, i: number) => (
                      <p key={i} className="text-[10px] text-text-secondary pl-5">
                        {a.url ? <a href={a.url} target="_blank" rel="noopener" className="hover:underline">{a.title?.slice(0, 80)}</a> : a.title?.slice(0, 80)}
                      </p>
                    ))}
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {tab === 'feed' && (
        <div className="space-y-1 max-h-[500px] overflow-y-auto">
          <div className="relative max-w-sm mb-2"><Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted" /><input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Filter..." className="input pl-7 py-1.5 text-[11px]" /></div>
          {talentFeed.slice(0, 30).map((a, i) => (
            <div key={i} className="card-hover p-3">
              <p className="text-[11px] text-text-primary">{a.url ? <a href={a.url} target="_blank" rel="noopener" className="hover:underline">{a.title}</a> : a.title}</p>
              <span className="text-[9px] text-text-muted">{a.source} &middot; {relativeTime(a.timestamp)}</span>
            </div>
          ))}
        </div>
      )}

      {tab === 'layoffs' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="card p-4 border border-rose/20">
            <h2 className="text-xs font-semibold text-rose-light mb-3"><TrendingDown size={14} className="inline mr-1" />Layoff Signals ({layoffCount})</h2>
            <div className="space-y-1 max-h-[350px] overflow-y-auto">
              {layoffArticles.slice(0, 15).map((a, i) => (<div key={i} className="py-1.5 border-b border-border last:border-0"><p className="text-[10px] text-text-primary">{a.url ? <a href={a.url} target="_blank" rel="noopener" className="hover:underline">{a.title}</a> : a.title}</p><span className="text-[9px] text-text-muted">{a.source}</span></div>))}
              {!layoffArticles.length && <p className="text-[10px] text-text-muted py-4 text-center">No signals</p>}
            </div>
          </div>
          <div className="card p-4 border border-emerald/20">
            <h2 className="text-xs font-semibold text-emerald-light mb-3"><TrendingUp size={14} className="inline mr-1" />Hiring Signals ({hiringCount})</h2>
            <div className="space-y-1 max-h-[350px] overflow-y-auto">
              {hiringArticles.slice(0, 15).map((a, i) => (<div key={i} className="py-1.5 border-b border-border last:border-0"><p className="text-[10px] text-text-primary">{a.url ? <a href={a.url} target="_blank" rel="noopener" className="hover:underline">{a.title}</a> : a.title}</p><span className="text-[9px] text-text-muted">{a.source}</span></div>))}
              {!hiringArticles.length && <p className="text-[10px] text-text-muted py-4 text-center">No signals</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
